<template>
  <v-container fluid class="apresentacoes-page">
    <v-row justify="center">
      <v-col cols="12" xl="10">
        <div class="page-header">
          <div>
            <p class="eyebrow">Repositório interno</p>
            <h1>Apresentações</h1>
            <p class="subtitle">
              Materiais HTML disponíveis para apresentação em tela cheia.
            </p>
          </div>
          <div class="header-actions">
            <v-btn color="primary" outlined to="/apresentacoes/editor">
              <v-icon left>mdi-code-tags</v-icon>
              Editor HTML
            </v-btn>
            <v-btn color="primary" depressed @click="load" :loading="loading">
              <v-icon left>mdi-refresh</v-icon>
              Atualizar
            </v-btn>
          </div>
        </div>

        <v-alert v-if="error" type="error" outlined class="mb-6">
          {{ error }}
        </v-alert>

        <v-row v-if="loading && !items.length">
          <v-col v-for="n in 3" :key="n" cols="12" md="4">
            <v-skeleton-loader type="card" />
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col
            v-for="item in items"
            :key="item.slug"
            cols="12"
            md="6"
            lg="4"
          >
            <v-card class="presentation-card" outlined>
              <v-card-text>
                <div class="card-topline">
                  <span>{{ item.tipo || "Apresentação" }}</span>
                  <span v-if="item.versao">{{ item.versao }}</span>
                </div>
                <h2>{{ item.titulo }}</h2>
                <p>{{ item.descricao }}</p>
                <div class="meta">
                  <span v-if="item.atualizadoEm">Atualizado em {{ item.atualizadoEm }}</span>
                  <span v-if="item.pasta">{{ item.pasta }}</span>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  color="primary"
                  :href="item.entry"
                  target="_blank"
                  rel="noopener"
                  depressed
                >
                  Abrir
                </v-btn>
                <v-btn
                  color="primary"
                  text
                  :to="{ name: 'apresentacoes-editor', params: { slug: item.slug } }"
                >
                  <v-icon left>mdi-pencil</v-icon>
                  Editar
                </v-btn>
                <v-spacer />
                <v-btn color="error" text @click="confirmArchive(item)">
                  <v-icon left>mdi-archive</v-icon>
                  Arquivar
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <v-alert
          v-if="!loading && !items.length && !error"
          type="info"
          outlined
          class="mt-4"
        >
          Nenhuma apresentação cadastrada.
        </v-alert>

        <v-dialog v-model="archiveDialog" max-width="520">
          <v-card>
            <v-card-title>Arquivar apresentacao?</v-card-title>
            <v-card-text>
              <strong>{{ archiveItem ? archiveItem.titulo : "" }}</strong> deixara de aparecer no catalogo. Os arquivos serao preservados.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="archiveDialog = false">Cancelar</v-btn>
              <v-btn color="error" depressed :loading="archiving" @click="archive">
                Arquivar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar.show" :timeout="5000">
          {{ snackbar.text }}
          <template v-slot:action="{ attrs }">
            <v-btn text v-bind="attrs" @click="snackbar.show = false">Fechar</v-btn>
          </template>
        </v-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: "ApresentacoesList",
  data() {
    return {
      loading: false,
      error: "",
      items: [],
      archiveDialog: false,
      archiveItem: null,
      archiving: false,
      snackbar: {
        show: false,
        text: "",
      },
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const data = await this.loadManifest();
        this.items = Array.isArray(data.apresentacoes)
          ? data.apresentacoes
          : [];
      } catch (err) {
        this.error = err.message || "Erro ao carregar apresentações.";
      } finally {
        this.loading = false;
      }
    },
    async loadManifest() {
      try {
        const response = await this.$http.get("/api/apresentacoes-editor/manifest");
        return response.data || {};
      } catch (apiError) {
        const response = await fetch("/Apresentacoes/manifest.json", {
          cache: "no-store",
        });
        if (!response.ok) {
          throw new Error("Não foi possível carregar o catálogo.");
        }
        return response.json();
      }
    },
    confirmArchive(item) {
      this.archiveItem = item;
      this.archiveDialog = true;
    },
    async archive() {
      if (!this.archiveItem) {
        return;
      }

      this.archiving = true;
      this.error = "";
      try {
        await this.$http.post(
          `/api/apresentacoes-editor/${this.archiveItem.slug}/arquivar`
        );
        this.items = this.items.filter((item) => item.slug !== this.archiveItem.slug);
        this.archiveDialog = false;
        this.snackbar = {
          show: true,
          text: "Apresentacao arquivada.",
        };
        this.archiveItem = null;
      } catch (err) {
        this.error = "Nao foi possivel arquivar a apresentacao.";
      } finally {
        this.archiving = false;
      }
    },
  },
};
</script>

<style scoped>
.apresentacoes-page {
  min-height: 100vh;
  background: #f4f7f8;
  padding: 40px 28px;
}

.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
}

.header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.eyebrow {
  margin: 0 0 8px;
  color: #087b73;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  color: #082b31;
  font-size: 42px;
  font-weight: 800;
  line-height: 1.05;
}

.subtitle {
  max-width: 680px;
  margin: 12px 0 0;
  color: #476369;
  font-size: 18px;
}

.presentation-card {
  height: 100%;
  border-radius: 8px;
  background: #fff;
}

.card-topline {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #0b8178;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

h2 {
  margin: 18px 0 10px;
  color: #0b2d32;
  font-size: 24px;
  line-height: 1.15;
}

.presentation-card p {
  min-height: 72px;
  color: #49656a;
  font-size: 15px;
  line-height: 1.45;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.meta span {
  border-radius: 999px;
  background: #e8f0f1;
  color: #34565c;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
}

@media (max-width: 760px) {
  .apresentacoes-page {
    padding: 24px 14px;
  }

  .page-header {
    align-items: stretch;
    flex-direction: column;
  }

  .header-actions {
    justify-content: flex-start;
  }

  h1 {
    font-size: 34px;
  }
}
</style>
